<template>
  <div class="p-2 sm:p-4 text-lexend min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] font-black text-indigo-600 tracking-widest">Recarga de Gas</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2">
          <Icon icon="mdi:chart-line" class="text-indigo-600" /> Reportes de Gas
        </h1>
      </div>
      <div class="flex gap-2 flex-wrap">
        <button @click="imprimirConsolidado" class="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center gap-2">
          <Icon icon="mdi:chart-arc" /> Reporte Consolidado
        </button>
        <button @click="imprimirReporte" class="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center gap-2">
          <Icon icon="mdi:printer" /> Imprimir PDF
        </button>
        <button @click="exportarExcel" class="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center gap-2">
          <Icon icon="mdi:microsoft-excel" /> Exportar Excel
        </button>
      </div>
    </div>

    <!-- TABS -->
    <div class="flex gap-2 mb-4 overflow-x-auto pb-1">
      <button
        v-for="t in tabs"
        :key="t.id"
        @click="tabActiva = t.id"
        :class="[
          'px-4 py-2 rounded-xl font-black text-xs tracking-widest whitespace-nowrap transition',
          tabActiva === t.id
            ? 'bg-indigo-600 text-white shadow-md'
            : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-gray-700 border dark:border-gray-700'
        ]"
      >
        <Icon :icon="t.icon" class="inline mr-1" /> {{ t.label }}
      </button>
    </div>

    <!-- Filtros (compartidos) -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 mb-4 dark:border-gray-700">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <!-- Estado (solo visible en Pedidos) -->
        <div v-if="tabActiva === 'pedidos'">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Estado</label>
          <select v-model="filtro" class="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-sm dark:text-white">
            <option value="todos">TODOS</option>
            <option value="pendientes">PENDIENTES</option>
            <option value="en_proceso">EN PROCESO</option>
            <option value="completados">COMPLETADOS</option>
            <option value="cancelados">CANCELADOS</option>
            <option value="por_cobrar">POR COBRAR</option>
            <option value="pagados">PAGADOS</option>
          </select>
        </div>

        <!-- Zona -->
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Zona</label>
          <select v-model="filtroZona" class="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-sm dark:text-white">
            <option value="">TODAS LAS ZONAS</option>
            <option v-for="z in zonasDisponibles" :key="z" :value="z">{{ z }}</option>
          </select>
        </div>

        <!-- Método de Pago -->
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Método de Pago</label>
          <select v-model="filtroMetodoPago" class="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-sm dark:text-white">
            <option value="">TODOS LOS MÉTODOS</option>
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia</option>
            <option value="pago_movil">Pago Móvil</option>
            <option value="zelle">Zelle</option>
            <option value="usdt">USDT</option>
          </select>
        </div>

        <!-- Fecha Desde -->
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Desde</label>
          <input type="date" v-model="fechaDesde"
            class="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-sm dark:text-white" />
        </div>

        <!-- Fecha Hasta -->
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Hasta</label>
          <div class="flex gap-2">
            <input type="date" v-model="fechaHasta"
              class="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-sm dark:text-white" />
            <button v-if="fechaDesde || fechaHasta" @click="limpiarFechas"
              class="px-3 py-2 bg-rose-100 hover:bg-rose-200 text-rose-600 rounded-xl font-black text-xs flex items-center justify-center">
              <Icon icon="mdi:close" />
            </button>
          </div>
        </div>
      </div>

      <!-- Accesos rápidos -->
      <div class="flex gap-2 flex-wrap mt-3 pt-3 border-t dark:border-gray-700">
        <button @click="setRangoRapido('hoy')" class="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">Hoy</button>
        <button @click="setRangoRapido('semana')" class="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">Esta semana</button>
        <button @click="setRangoRapido('mes')" class="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">Este mes</button>
        <button @click="setRangoRapido('mes_anterior')" class="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">Mes anterior</button>
        <button @click="setRangoRapido('año')" class="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">Este año</button>
      </div>
    </div>

    <!-- Stats Cards (compartidas) -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 dark:border-gray-700">
        <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Pedidos</p>
        <p class="text-2xl font-black text-gray-900 dark:text-white mt-1">{{ pedidosParaReporte.length }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 dark:border-gray-700">
        <p class="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Completados</p>
        <p class="text-2xl font-black text-emerald-600 mt-1">{{ stats.completados }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 dark:border-gray-700">
        <p class="text-[10px] font-black text-blue-500 uppercase tracking-widest">Facturado USD</p>
        <p class="text-xl font-black text-blue-600 mt-1">${{ formatearMoneda(stats.totalFacturadoUsd) }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 dark:border-gray-700">
        <p class="text-[10px] font-black text-purple-500 uppercase tracking-widest">Facturado Bs</p>
        <p class="text-xl font-black text-purple-600 mt-1">Bs. {{ formatearMoneda(stats.totalFacturadoBs) }}</p>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- TAB: PEDIDOS -->
    <!-- ============================================ -->
    <div v-if="tabActiva === 'pedidos'" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-5 dark:border-gray-700">
      <h2 class="text-sm font-black text-gray-900 dark:text-white mb-3">
        Pedidos ({{ pedidosFiltrados.length }})
      </h2>
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th class="px-3 py-2 text-left font-black text-gray-400">N° Pedido</th>
              <th class="px-3 py-2 text-left font-black text-gray-400">Cliente</th>
              <th class="px-3 py-2 text-left font-black text-gray-400">Zona</th>
              <th class="px-3 py-2 text-center font-black text-gray-400">Estado</th>
              <th class="px-3 py-2 text-center font-black text-gray-400">Método Pago</th>
              <th class="px-3 py-2 text-center font-black text-gray-400">Cant.</th>
              <th class="px-3 py-2 text-center font-black text-gray-400">Kilos</th>
              <th class="px-3 py-2 text-center font-black text-gray-400">Litros</th>
              <th class="px-3 py-2 text-right font-black text-gray-400">Total USD</th>
              <th class="px-3 py-2 text-right font-black text-gray-400">Total Bs</th>
            </tr>
          </thead>
          <tbody class="divide-y dark:divide-gray-700">
            <tr v-if="pedidosFiltrados.length === 0">
              <td colspan="10" class="px-3 py-8 text-center text-gray-400">No hay pedidos</td>
            </tr>
            <tr v-for="p in pedidosFiltrados" :key="p.idPedidoRecarga" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30">
              <td class="px-3 py-2 font-bold text-gray-900 dark:text-white">{{ p.numeroPedido }}</td>
              <td class="px-3 py-2 text-gray-700 dark:text-gray-300">{{ p.nombreCliente }}</td>
              <td class="px-3 py-2 text-gray-700 dark:text-gray-300">{{ p.nombreZona }}</td>
              <td class="px-3 py-2 text-center">
                <span :class="['px-2 py-0.5 rounded text-[9px] font-black uppercase', colorEstadoPedido(p.estado)]">{{ p.estado }}</span>
              </td>
              <td class="px-3 py-2 text-center">
                <span :class="['px-2 py-0.5 rounded text-[9px] font-black uppercase', colorMetodoPago(p)]">
                  {{ labelMetodoPago(p) }}
                </span>
              </td>
              <td class="px-3 py-2 text-center font-bold text-gray-800 dark:text-white">{{ getCantidadTotal(p) }}</td>
              <td class="px-3 py-2 text-center font-bold text-orange-600">{{ getKilosTotales(p) }} kg</td>
              <td class="px-3 py-2 text-center font-bold text-cyan-600">{{ getLitrosTotales(p) }} L</td>
              <td class="px-3 py-2 text-right font-black text-emerald-600">${{ formatearMoneda(p.totalUsd) }}</td>
              <td class="px-3 py-2 text-right font-black text-blue-600">Bs. {{ formatearMoneda(p.totalBs) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- TAB: POR ZONA -->
    <!-- ============================================ -->
    <div v-if="tabActiva === 'zona'" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-5 dark:border-gray-700">
      <h2 class="text-sm font-black text-gray-900 dark:text-white mb-3">
        Distribución por Zona Geográfica
      </h2>

      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-indigo-600 text-white">
            <tr>
              <th class="px-3 py-2 text-left font-black">Zona Geográfica</th>
              <th class="px-3 py-2 text-center font-black">Clientes Únicos</th>
              <th class="px-3 py-2 text-center font-black">% Clientes</th>
              <th class="px-3 py-2 text-right font-black">Monto Consumido</th>
              <th class="px-3 py-2 text-center font-black">% Monto</th>
              <th class="px-3 py-2 text-center font-black">Compras</th>
              <th class="px-3 py-2 text-center font-black">Ticket Prom.</th>
            </tr>
          </thead>
          <tbody class="divide-y dark:divide-gray-700">
            <tr v-if="reportePorZona.zonas.length === 0">
              <td colspan="7" class="px-3 py-8 text-center text-gray-400">No hay datos</td>
            </tr>
            <tr v-for="z in reportePorZona.zonas" :key="z.zona" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30">
              <td class="px-3 py-2 font-bold text-gray-900 dark:text-white">{{ z.zona }}</td>
              <td class="px-3 py-2 text-center text-gray-700 dark:text-gray-300">
                {{ z.clientesUnicos }} <span class="text-gray-400">({{ z.compras }} compras)</span>
              </td>
              <td class="px-3 py-2 text-center font-bold text-indigo-600">{{ z.pctClientes.toFixed(1) }}%</td>
              <td class="px-3 py-2 text-right font-black text-gray-900 dark:text-white text-sm">
                {{ formatearMoneda(z.montoBs) }}
              </td>
              <td class="px-3 py-2 text-center font-bold text-emerald-600">{{ z.pctMonto.toFixed(2) }}%</td>
              <td class="px-3 py-2 text-center text-gray-700 dark:text-gray-300">{{ z.compras }}</td>
              <td class="px-3 py-2 text-center text-gray-700 dark:text-gray-300">
                Bs. {{ formatearMoneda(z.ticketPromedio) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-900 text-white font-black">
            <tr>
              <td class="px-3 py-3">TOTAL GENERAL</td>
              <td class="px-3 py-3 text-center">
                {{ reportePorZona.totales.clientesUnicos }}
                ({{ reportePorZona.totales.totalCompras }} reg.)
              </td>
              <td class="px-3 py-3 text-center">100%</td>
              <td class="px-3 py-3 text-right">{{ formatearMoneda(reportePorZona.totales.montoBs) }}</td>
              <td class="px-3 py-3 text-center">100%</td>
              <td class="px-3 py-3 text-center">{{ reportePorZona.totales.totalCompras }}</td>
              <td class="px-3 py-3 text-center">—</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Barras comparativas -->
      <div class="mt-5 pt-4 border-t dark:border-gray-700">
        <h3 class="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
          Comparativo de Ingresos por Zona
        </h3>
        <div class="space-y-2">
          <div v-for="(z, i) in reportePorZona.zonas.slice(0, 8)" :key="z.zona">
            <div class="flex justify-between text-[11px] font-black text-gray-700 dark:text-gray-300 mb-1">
              <span>{{ z.zona }}</span>
              <span>Bs. {{ formatearMoneda(z.montoBs) }}</span>
            </div>
            <div class="h-5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
              <div
                :class="['h-full', i === 0 ? 'bg-indigo-600' : (i === 1 ? 'bg-orange-500' : 'bg-gray-400')]"
                :style="{ width: (z.montoBs / maxMontoZona) * 100 + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- TAB: POR DELIVERY -->
    <!-- ============================================ -->
    <div v-if="tabActiva === 'delivery'" class="space-y-4">

      <!-- ⭐ SELECTOR DE DELIVERY INDIVIDUAL -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border-2 border-amber-300 dark:border-amber-700 p-4">
        <div class="flex items-center gap-2 mb-3">
          <Icon icon="mdi:account-search" class="text-amber-500 text-xl" />
          <label class="text-xs font-black text-gray-700 dark:text-gray-200 uppercase tracking-widest">
            Seleccionar Delivery
          </label>
        </div>
        <select
          v-model="filtroDelivery"
          class="w-full px-4 py-3 bg-amber-50 dark:bg-gray-900/50 border-2 border-amber-200 dark:border-gray-700 focus:border-amber-500 rounded-xl font-black text-sm dark:text-white"
        >
          <option value="">👥 TODOS LOS DELIVERIES ({{ deliveriesDisponibles.length }})</option>
          <option
            v-for="d in deliveriesDisponibles"
            :key="d.idDelivery"
            :value="d.idDelivery"
          >
            🛵 {{ d.nombre }} — {{ d.pedidos }} pedidos — ${{ formatearMoneda(d.comision) }} comisión
          </option>
        </select>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-2 font-bold">
          {{ filtroDelivery
            ? '🔍 Mostrando reporte individual del delivery seleccionado'
            : '📊 Mostrando reporte de TODOS los deliveries' }}
        </p>
      </div>

      <!-- Header -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 dark:border-gray-700">
        <h2 class="text-sm font-black text-gray-900 dark:text-white">
          Fuerza Comercial:
          <span class="text-indigo-600">{{ reportePorDeliveryFiltrado.length }}</span>
          repartidor{{ reportePorDeliveryFiltrado.length === 1 ? '' : 'es' }}
        </h2>
        <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
          Cobertura: {{ zonasCubiertasPorDelivery }} zonas
        </p>
      </div>

      <!-- Sin datos -->
      <div v-if="reportePorDeliveryFiltrado.length === 0"
        class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-8 dark:border-gray-700 text-center text-gray-400">
        No hay deliveries con pedidos asignados
      </div>

      <!-- Tarjetas -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div v-for="(d, i) in reportePorDeliveryFiltrado" :key="d.idDelivery"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border dark:border-gray-700 overflow-hidden">
          <!-- Header con gradiente -->
          <div :class="[
            'px-4 py-3 flex justify-between items-center text-white',
            i % 2 === 0
              ? 'bg-gradient-to-r from-indigo-600 to-blue-500'
              : 'bg-gradient-to-r from-gray-600 to-gray-500'
          ]">
            <div>
              <p class="font-black text-sm">{{ d.nombre }}</p>
              <p class="text-[10px] opacity-80">{{ d.pedidos }} pedidos</p>
            </div>
            <span class="bg-white/20 px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest">
              {{ d.zonasCount }} zona{{ d.zonasCount === 1 ? '' : 's' }}
            </span>
          </div>

          <!-- Stats -->
          <div class="p-4 border-b dark:border-gray-700">
            <div class="grid grid-cols-3 gap-2 text-center">
              <div>
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Pedidos</p>
                <p class="text-lg font-black text-gray-900 dark:text-white">{{ d.pedidos }}</p>
              </div>
              <div>
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Clientes</p>
                <p class="text-lg font-black text-gray-900 dark:text-white">{{ d.clientesUnicos }}</p>
              </div>
              <div>
                <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">Ticket</p>
                <p class="text-sm font-black text-gray-900 dark:text-white">Bs. {{ formatearMoneda(d.ticketPromedio) }}</p>
              </div>
            </div>
          </div>

          <!-- Desglose por zona -->
          <div class="p-4">
            <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">
              Desglose por Zona
            </p>
            <div v-for="z in d.zonasDetalle" :key="z.zona"
              class="flex justify-between items-center py-1 text-[11px] dark:text-gray-300">
              <span class="font-bold">{{ z.zona }}</span>
              <span class="text-gray-500">{{ z.clientesUnicos }} cli.</span>
              <span class="font-black text-gray-900 dark:text-white">Bs. {{ formatearMoneda(z.montoBs) }}</span>
            </div>
          </div>

          <!-- ⭐ Comisión individual -->
          <div class="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-t dark:border-gray-700">
            <div class="flex justify-between items-center">
              <span class="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest">
                Comisión Total
              </span>
              <span class="text-lg font-black text-amber-700 dark:text-amber-300">
                ${{ formatearMoneda(d.comision) }}
              </span>
            </div>
            <div class="mt-2 text-[9px] text-amber-600/80 dark:text-amber-400/80 space-y-0.5">
              <div v-for="(tarifa, kg) in TARIFAS_COMISION" :key="kg" class="flex justify-between">
                <span>Cilindro {{ kg }} kg × {{ d.cilindrosPorKg[kg] || 0 }}</span>
                <span>${{ (tarifa * (d.cilindrosPorKg[kg] || 0)).toFixed(2) }}</span>
              </div>
            </div>
            <div class="mt-2 grid grid-cols-4 gap-1 text-[9px] text-center">
              <div v-for="(cant, kg) in d.cilindrosPorKg" :key="kg"
                class="bg-white dark:bg-gray-800 rounded py-1">
                <p class="font-black text-gray-400">{{ kg }}kg</p>
                <p class="font-black text-gray-900 dark:text-white">{{ cant }}</p>
              </div>
            </div>
          </div>

          <!-- Subtotal -->
          <div class="bg-gray-900 text-white px-4 py-2 flex justify-between items-center text-xs font-black">
            <span>SUBTOTAL {{ d.nombre.toUpperCase() }}</span>
            <span>
              {{ d.pedidos }} ped. · Bs. {{ formatearMoneda(d.montoBs) }} ·
              Comisión ${{ formatearMoneda(d.comision) }}
            </span>
          </div>
        </div>
      </div>

      <!-- ⭐ TABLA RESUMEN DE COMISIONES -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-5 dark:border-gray-700">
        <h2 class="text-sm font-black text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Icon icon="mdi:cash-multiple" class="text-amber-500" />
          Reporte de Comisiones por Delivery
        </h2>
        <div class="overflow-x-auto">
          <table class="w-full text-xs">
            <thead class="bg-amber-500 text-white">
              <tr>
                <th class="px-3 py-2 text-left font-black">Delivery</th>
                <th class="px-3 py-2 text-center font-black">Pedidos</th>
                <th class="px-3 py-2 text-center font-black">10 kg</th>
                <th class="px-3 py-2 text-center font-black">18 kg</th>
                <th class="px-3 py-2 text-center font-black">27 kg</th>
                <th class="px-3 py-2 text-center font-black">43 kg</th>
                <th class="px-3 py-2 text-right font-black">Comisión USD</th>
              </tr>
            </thead>
            <tbody class="divide-y dark:divide-gray-700">
              <tr v-if="reportePorDeliveryFiltrado.length === 0">
                <td colspan="7" class="px-3 py-8 text-center text-gray-400">No hay deliveries</td>
              </tr>
              <tr v-for="d in reportePorDeliveryFiltrado" :key="d.idDelivery"
                class="hover:bg-amber-50/30 dark:hover:bg-gray-700/30">
                <td class="px-3 py-2 font-bold text-gray-900 dark:text-white">{{ d.nombre }}</td>
                <td class="px-3 py-2 text-center">{{ d.pedidos }}</td>
                <td class="px-3 py-2 text-center">{{ d.cilindrosPorKg[10] || 0 }}</td>
                <td class="px-3 py-2 text-center">{{ d.cilindrosPorKg[18] || 0 }}</td>
                <td class="px-3 py-2 text-center">{{ d.cilindrosPorKg[27] || 0 }}</td>
                <td class="px-3 py-2 text-center">{{ d.cilindrosPorKg[43] || 0 }}</td>
                <td class="px-3 py-2 text-right font-black text-amber-600">
                  ${{ formatearMoneda(d.comision) }}
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-900 text-white font-black">
              <tr>
                <td class="px-3 py-3">TOTAL</td>
                <td class="px-3 py-3 text-center">
                  {{ reportePorDeliveryFiltrado.reduce((s, d) => s + d.pedidos, 0) }}
                </td>
                <td class="px-3 py-3 text-center">
                  {{ reportePorDeliveryFiltrado.reduce((s, d) => s + (d.cilindrosPorKg[10] || 0), 0) }}
                </td>
                <td class="px-3 py-3 text-center">
                  {{ reportePorDeliveryFiltrado.reduce((s, d) => s + (d.cilindrosPorKg[18] || 0), 0) }}
                </td>
                <td class="px-3 py-3 text-center">
                  {{ reportePorDeliveryFiltrado.reduce((s, d) => s + (d.cilindrosPorKg[27] || 0), 0) }}
                </td>
                <td class="px-3 py-3 text-center">
                  {{ reportePorDeliveryFiltrado.reduce((s, d) => s + (d.cilindrosPorKg[43] || 0), 0) }}
                </td>
                <td class="px-3 py-3 text-right text-amber-400">
                  ${{ formatearMoneda(comisionTotalDelivery) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import {
  obtenerPedidosRecarga,
  obtenerPagosPedidos,
  obtenerAsignaciones,
  obtenerDeliveries,
  colorEstadoPedido
} from '@/api/gas.js';
import {
  calcularEstadisticasGas,
  filtrarPedidosGas,
  formatearNumero,
  imprimirReporteGas,
  exportarPedidosGasExcel
} from '@/utils/reporteGasGenerator';
import {
  calcularReportePorZona,
  calcularReportePorDelivery,
  calcularComisionPedido,
  calcularComisionTotal,
  TARIFAS_COMISION
} from '@/utils/reporteGasConsolidado';
import { generarHTMLReporteConsolidado } from '@/utils/reporteGasConsolidadoHTML';

export default {
  name: 'ReportesGas',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      pedidos: [],
      pagos: [],
      asignaciones: [],
      deliveries: [],
      loading: false,
      tabActiva: 'pedidos',
      tabs: [
        { id: 'pedidos', label: 'Pedidos', icon: 'mdi:clipboard-list' },
        { id: 'zona', label: 'Por Zona', icon: 'mdi:map-marker-multiple' },
        { id: 'delivery', label: 'Por Delivery', icon: 'mdi:truck-fast' }
      ],
      filtro: 'todos',
      filtroZona: '',
      filtroMetodoPago: '',
      filtroDelivery: '',
      fechaDesde: '',
      fechaHasta: ''
    };
  },
  computed: {
    pedidosFiltrados() {
      let lista = filtrarPedidosGas(this.pedidos, this.filtro, this.pagos);

      if (this.filtroZona) {
        lista = lista.filter(p => p.nombreZona === this.filtroZona);
      }

      if (this.filtroMetodoPago) {
        lista = lista.filter(p => this.getMetodoPagoRaw(p) === this.filtroMetodoPago.toLowerCase());
      }

      if (this.fechaDesde || this.fechaHasta) {
        lista = lista.filter(p => {
          const fecha = p.fechaPedido || p.fechaCompletado;
          if (!fecha) return false;
          const fechaPedido = new Date(fecha);
          fechaPedido.setHours(0, 0, 0, 0);
          if (this.fechaDesde) {
            const desde = new Date(this.fechaDesde + 'T00:00:00');
            if (fechaPedido < desde) return false;
          }
          if (this.fechaHasta) {
            const hasta = new Date(this.fechaHasta + 'T23:59:59');
            if (fechaPedido > hasta) return false;
          }
          return true;
        });
      }

      return lista;
    },

    // ⭐ Pedidos que se usarán para imprimir/exportar (respeta filtro de delivery)
    pedidosParaReporte() {
      if (!this.filtroDelivery) return this.pedidosFiltrados;

      const idsPedidos = new Set(
        this.asignaciones
          .filter(a => String(a.idDelivery ?? a.deliveryId ?? a.idDeliveryFK) === String(this.filtroDelivery))
          .map(a => String(a.idPedidoRecarga ?? a.idPedido ?? a.pedidoId))
      );

      return this.pedidosFiltrados.filter(p =>
        idsPedidos.has(String(p.idPedidoRecarga ?? p.idPedido))
      );
    },

    stats() {
      return calcularEstadisticasGas(this.pedidosParaReporte);
    },

    zonasDisponibles() {
      const set = new Set();
      this.pedidos.forEach(p => { if (p.nombreZona) set.add(p.nombreZona); });
      return Array.from(set).sort();
    },

    reportePorZona() {
      return calcularReportePorZona(this.pedidosFiltrados);
    },

    reportePorDelivery() {
      return calcularReportePorDelivery(this.pedidosFiltrados, this.asignaciones, this.deliveries);
    },

    deliveriesDisponibles() {
      return this.reportePorDelivery.deliveries;
    },

    reportePorDeliveryFiltrado() {
      if (!this.filtroDelivery) return this.reportePorDelivery.deliveries;
      return this.reportePorDelivery.deliveries.filter(
        d => String(d.idDelivery) === String(this.filtroDelivery)
      );
    },

    comisionTotalDelivery() {
      return this.reportePorDeliveryFiltrado.reduce((s, d) => s + (d.comision || 0), 0);
    },

    maxMontoZona() {
      if (this.reportePorZona.zonas.length === 0) return 1;
      return Math.max(...this.reportePorZona.zonas.map(z => z.montoBs));
    },

    zonasCubiertasPorDelivery() {
      const set = new Set();
      this.reportePorDelivery.deliveries.forEach(d => {
        d.zonas.forEach(z => set.add(z));
      });
      return set.size;
    }
  },
  async mounted() {
    await this.load();
  },
  methods: {
    colorEstadoPedido,
    formatearNumero,
    calcularComisionPedido,
    calcularComisionTotal,

    formatearMoneda(valor) {
      if (valor === null || valor === undefined) return '0.00';
      const num = Number(valor);
      if (isNaN(num)) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    },

    limpiarFechas() {
      this.fechaDesde = '';
      this.fechaHasta = '';
    },

    setRangoRapido(tipo) {
      const hoy = new Date();
      const yyyy = hoy.getFullYear();
      const mm = String(hoy.getMonth() + 1).padStart(2, '0');
      const dd = String(hoy.getDate()).padStart(2, '0');

      const formatear = (fecha) => {
        const y = fecha.getFullYear();
        const m = String(fecha.getMonth() + 1).padStart(2, '0');
        const d = String(fecha.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      switch (tipo) {
        case 'hoy':
          this.fechaDesde = `${yyyy}-${mm}-${dd}`;
          this.fechaHasta = `${yyyy}-${mm}-${dd}`;
          break;
        case 'semana': {
          const dia = hoy.getDay() || 7;
          const lunes = new Date(hoy);
          lunes.setDate(hoy.getDate() - (dia - 1));
          const domingo = new Date(lunes);
          domingo.setDate(lunes.getDate() + 6);
          this.fechaDesde = formatear(lunes);
          this.fechaHasta = formatear(domingo);
          break;
        }
        case 'mes': {
          const primerDia = new Date(yyyy, hoy.getMonth(), 1);
          const ultimoDia = new Date(yyyy, hoy.getMonth() + 1, 0);
          this.fechaDesde = formatear(primerDia);
          this.fechaHasta = formatear(ultimoDia);
          break;
        }
        case 'mes_anterior': {
          const primerDia = new Date(yyyy, hoy.getMonth() - 1, 1);
          const ultimoDia = new Date(yyyy, hoy.getMonth(), 0);
          this.fechaDesde = formatear(primerDia);
          this.fechaHasta = formatear(ultimoDia);
          break;
        }
        case 'año':
          this.fechaDesde = `${yyyy}-01-01`;
          this.fechaHasta = `${yyyy}-12-31`;
          break;
      }
    },

    getMetodoPagoRaw(pedido) {
      if (!pedido) return '';
      const idPedidoActual = pedido.idPedidoRecarga ?? pedido.idPedido;
      const pagosDelPedido = this.pagos.filter(pg => {
        const idPagoPedido = pg.idPedidoRecarga ?? pg.idPedido ?? pg.pedidoId ?? pg.idPedidoRecargaFK;
        if (idPagoPedido && idPedidoActual && String(idPagoPedido) === String(idPedidoActual)) return true;
        if (pg.numeroPedido && pedido.numeroPedido) {
          if (String(pg.numeroPedido).trim() === String(pedido.numeroPedido).trim()) return true;
        }
        return false;
      });
      for (const pg of pagosDelPedido) {
        const metodo = pg.metodoPago ?? pg.metodo_pago ?? pg.formaPago ?? pg.tipoPago;
        if (metodo && String(metodo).trim() !== '') return String(metodo).toLowerCase().trim();
      }
      const candidatos = [
        pedido.metodoPago, pedido.metodoPagoCliente, pedido.metodo_pago,
        pedido.formaPago, pedido.tipoPago,
        pedido.cliente?.metodoPago, pedido.detalles?.[0]?.metodoPago,
      ];
      for (const v of candidatos) {
        if (v && String(v).trim() !== '') return String(v).toLowerCase().trim();
      }
      return '';
    },

    getCantidadTotal(pedido) {
      if (!pedido || !Array.isArray(pedido.detalles)) return 0;
      return pedido.detalles.reduce((sum, d) => sum + (Number(d.cantidad) || 0), 0);
    },

    getKilosTotales(pedido) {
      if (!pedido || !Array.isArray(pedido.detalles)) return '0';
      const t = pedido.detalles.reduce(
        (sum, d) => sum + ((Number(d.kilogramos) || 0) * (Number(d.cantidad) || 0)), 0
      );
      return formatearNumero(t, 1);
    },

    getLitrosTotales(pedido) {
      if (!pedido || !Array.isArray(pedido.detalles)) return '0';
      const t = pedido.detalles.reduce(
        (sum, d) => sum + ((Number(d.litros) || 0) * (Number(d.cantidad) || 0)), 0
      );
      return formatearNumero(t, 1);
    },

    labelMetodoPago(p) {
      const metodo = this.getMetodoPagoRaw(p);
      const map = {
        'efectivo': 'Efectivo',
        'transferencia': 'Transferencia',
        'pago_movil': 'Pago Móvil',
        'pago movil': 'Pago Móvil',
        'pagomovil': 'Pago Móvil',
        'zelle': 'Zelle',
        'usdt': 'USDT',
      };
      return map[metodo] || (metodo ? metodo.toUpperCase() : 'Sin definir');
    },

    colorMetodoPago(p) {
      const metodo = this.getMetodoPagoRaw(p);
      const colores = {
        'efectivo': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
        'transferencia': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        'pago_movil': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'pago movil': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'pagomovil': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'zelle': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
        'usdt': 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      };
      return colores[metodo] || 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300';
    },

    async load() {
      this.loading = true;
      try {
        const [resPedidos, resPagos, resAsig, resDeliv] = await Promise.all([
          obtenerPedidosRecarga(),
          obtenerPagosPedidos(),
          obtenerAsignaciones(),
          obtenerDeliveries()
        ]);

        if (resPedidos?.success) {
          this.pedidos = Array.isArray(resPedidos.data) ? resPedidos.data : [];
        }

        if (resPagos?.success) {
          this.pagos = Array.isArray(resPagos.data) ? resPagos.data : [];
        }

        if (resAsig?.success) {
          this.asignaciones = Array.isArray(resAsig.data) ? resAsig.data : [];
        }

        if (resDeliv?.success) {
          this.deliveries = Array.isArray(resDeliv.data) ? resDeliv.data : [];
        }

        console.log('📊 Datos cargados:', {
          pedidos: this.pedidos.length,
          pagos: this.pagos.length,
          asignaciones: this.asignaciones.length,
          deliveries: this.deliveries.length
        });
      } catch (error) {
        console.error('💥 Excepción en load:', error);
        this.toast.error(error?.message || 'Error de conexión');
      } finally {
        this.loading = false;
      }
    },

    async imprimirReporte() {
      if (this.pedidosParaReporte.length === 0) {
        this.toast.warning('No hay pedidos para imprimir');
        return;
      }
      const result = await imprimirReporteGas(
        this.pedidosParaReporte,
        null,
        'todos',
        this.pagos
      );
      if (result) this.toast.success('Reporte generado');
    },

    async imprimirConsolidado() {
      if (this.pedidosParaReporte.length === 0) {
        this.toast.warning('No hay pedidos para el reporte consolidado');
        return;
      }

      let nombreDeliveryFiltro = '';
      if (this.filtroDelivery) {
        const d = this.deliveriesDisponibles.find(
          x => String(x.idDelivery) === String(this.filtroDelivery)
        );
        if (d) nombreDeliveryFiltro = d.nombre;
      }

      const html = generarHTMLReporteConsolidado(
        this.pedidosParaReporte,
        this.asignaciones,
        this.deliveries,
        {
          desde: this.fechaDesde,
          hasta: this.fechaHasta,
          zona: this.filtroZona,
          metodoPago: this.filtroMetodoPago,
          delivery: nombreDeliveryFiltro
        }
      );

      const ventana = window.open('', '_blank', 'width=1200,height=800');
      if (!ventana) {
        this.toast.error('Permite las ventanas emergentes para ver el reporte');
        return;
      }
      ventana.document.write(html);
      ventana.document.close();
      ventana.focus();
      this.toast.success('Reporte consolidado generado');
    },

    exportarExcel() {
      if (this.pedidosParaReporte.length === 0) {
        this.toast.warning('No hay pedidos para exportar');
        return;
      }
      const result = exportarPedidosGasExcel(
        this.pedidosParaReporte,
        'gas_reporte',
        'todos',
        this.pagos
      );
      if (result.success) {
        this.toast.success(`Excel generado: ${result.filename}`);
      } else {
        this.toast.error(result.error || 'Error al exportar');
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
</style>